import { useCallback, useEffect, useRef, useState } from 'react'
import throttle from 'lodash/throttle'
import { useActivate, useUnactivate } from 'umi'
import { useInterval, useHover } from 'ahooks'

export const useClickOutside = ({ rootEl, el, handler }) => {
    const $this = useRef({})

    Object.assign($this.current, { rootEl, el, handler })

    const documentHandler = useCallback(e => {
        const { el, handler } = $this.current

        const $el = typeof el === 'function' ? el() : el

        if ($el && !$el.contains(e.target)) {
            typeof handler === 'function' && handler()
        }
    }, [])

    const initEvent = useCallback(() => {
        const { rootEl } = $this.current

        const $rootEl = typeof rootEl === 'function' ? rootEl() : rootEl

        if ($rootEl) {
            $rootEl.addEventListener('click', documentHandler)
        }
    }, [])

    const removeEvent = useCallback(() => {
        const { rootEl } = $this.current

        const $rootEl = typeof rootEl === 'function' ? rootEl() : rootEl

        if ($rootEl) {
            $rootEl.removeEventListener('click', documentHandler)
        }
    }, [])

    useEffect(() => {
        initEvent()

        return () => {
            removeEvent()
        }
    }, [initEvent, removeEvent])

    return {
        initEvent,
        removeEvent
    }
}

export const useScroll = ({ target }) => {
    const getTarget = () => {
        return typeof target === 'function' ? target() : target
    }

    const scrollTo = ({ scrollTop }) => {
        const $el = getTarget()

        if ($el) {
            $el.scrollTop = scrollTop
        }
    }

    const scrollTop = () => scrollTo(0)

    return { scrollTop, scrollTo }
}

export const useLock = () => {
    const $lock = useRef(false)

    const lock = () => {
        $lock.current = true
    }

    const unlock = () => {
        $lock.current = false
    }

    return {
        checkIsLock: () => $lock.current,
        lock,
        unlock
    }
}

export const useInit = (ms = 200) => {
    const [init, setInit] = useState(false)

    useEffect(() => {
        setTimeout(() => {
            // eslint-disable-next-line max-nested-callbacks
            setInit(true)
        }, ms)
    }, [ms])

    return [init, setInit]
}

export const useLazyInit = ({ shouldInit, ms = 400, callback }) => {
    const $this = useRef({
        isUnMount: false
    })
    const [isInit, setIsInit] = useState(false)

    useEffect(() => {
        return () => {
            if ($this.current.t) {
                clearTimeout($this.current.t)
            }
        }
    }, [])

    useEffect(() => {
        if (shouldInit && !isInit) {
            if ($this.current.t) {
                clearTimeout($this.current.t)
            }
            $this.current.t = setTimeout(() => {
                if ($this.current.isUnMount) {
                    return
                }

                setIsInit(true)

                if (typeof callback === 'function') {
                    callback()
                }
            }, ms)
        }
    }, [shouldInit, ms])

    return [isInit, setIsInit]
}

export const useHeckKeepAliveSavePosition = () => {
    const scrollHeight = useRef(null)

    const addListenPageScroll = () => {
        const temp = document.getElementById('real-root')?.scrollTop
        scrollHeight.current = temp
    }

    useUnactivate(() => {
        if (document.getElementById('real-root')) {
            document.getElementById('real-root').onscroll = undefined
        }
    })

    useEffect(() => {
        if (document.getElementById('real-root')) {
            document.getElementById('real-root').onscroll = throttle(addListenPageScroll, 200)
        }
        return () => {
            if (document.getElementById('real-root')) {
                document.getElementById('real-root').onscroll = undefined
            }
        }
    }, [])

    useActivate(() => {
        try {
            if (document.getElementById('real-root')) {
                document.getElementById('real-root').onscroll = throttle(addListenPageScroll, 200)
                document.getElementById('real-root').scrollTop = scrollHeight.current
            }
        } catch (error) {
            console.error('catchError', error)
        }
    })
}

export const useHeckKeepAliveAffixChange = () => {
    const $pageRef = useRef()
    useActivate(() => {
        setTimeout(() => {
            $pageRef.current.className = ''
        }, 10)
    })

    useUnactivate(() => {
        $pageRef.current.className = 'hide-affix'
    })
    return [$pageRef]
}
//  dom
//  delay 毫秒

export const useHoverRefTimeOut = ({ dom }) => {
    const isHovering = useHover(dom)
    const [count, setCount] = useState(0)
    const clear = useInterval(() => {
        if (isHovering) {
            setCount(count + 1)
        } else {
            setCount(0)
        }
    }, 100)

    return [count, clear]
}
